import { TFunction } from "i18next";
import moment from "moment";
import { NetworkScanState } from "../codegen/graphql";

export interface NetworkScanStateConfig {
    state: NetworkScanState;
    /** i18n key of the short result label ("Passed") */
    label: string;
    /** i18n key of the one-line explanation shown on the choice tile */
    hint: string;
    /** Signal colour (dot on the choice tile, chip decorator, detail tab) */
    color: string;
    /** Background of the result chip */
    chipBg: string;
    /** Text colour of the result chip */
    chipText: string;
}

/**
 * Traffic light of the documented network scan (IBM-1039). Order is the
 * display order of the choice tiles; only GREEN admits a new IoT connection.
 * Chip colours reuse the IoT status palette (RUNNING / WARNING / ERROR).
 */
export const NETWORK_SCAN_STATE_CONFIG: NetworkScanStateConfig[] = [
    { state: NetworkScanState.GREEN,  label: "Passed",               hint: "Network fit for an IoT connection",        color: "#599460", chipBg: "#BDE3CA", chipText: "#304933" },
    { state: NetworkScanState.ORANGE, label: "Passed with findings", hint: "Findings need attention before connecting", color: "#FFC860", chipBg: "#FFDC99", chipText: "#333333" },
    { state: NetworkScanState.RED,    label: "Failed",               hint: "Network not fit for an IoT connection",    color: "#CC0000", chipBg: "#EE8E8E", chipText: "#333333" },
];

const CONFIG_BY_STATE: Record<string, NetworkScanStateConfig> = Object.fromEntries(
    NETWORK_SCAN_STATE_CONFIG.map((c) => [c.state, c])
);

export interface NetworkScanChipColors {
    bg: string;
    text: string;
}

const FALLBACK_CHIP_COLORS: NetworkScanChipColors = { bg: "#F4F4F4", text: "#333333" };
const FALLBACK_COLOR = "#7F7F7F";

type Translate = TFunction | ((key: string) => string);

const humanize = (value: string): string => {
    const humanized = value.replace(/_/g, " ").toLowerCase();
    return humanized.charAt(0).toUpperCase() + humanized.slice(1);
};

/**
 * Localized result label. `null`/`undefined` reads as "Not documented"; an
 * enum value the frontend does not know yet is humanized instead of leaking
 * the raw `SCREAMING_SNAKE` string.
 */
export const getNetworkScanStateLabel = (state: string | null | undefined, t: Translate): string => {
    if (!state) return t("Not documented") as string;
    const config = CONFIG_BY_STATE[state];
    return config ? (t(config.label) as string) : humanize(state);
};

/** One-line explanation of a result; empty for unknown values. */
export const getNetworkScanStateHint = (state: string | null | undefined, t: Translate): string => {
    const config = state ? CONFIG_BY_STATE[state] : undefined;
    return config ? (t(config.hint) as string) : "";
};

/** Chip background/text colours for a result; neutral grey when undocumented. */
export const getNetworkScanChipColors = (state: string | null | undefined): NetworkScanChipColors => {
    const config = state ? CONFIG_BY_STATE[state] : undefined;
    return config ? { bg: config.chipBg, text: config.chipText } : FALLBACK_CHIP_COLORS;
};

/** Signal colour of a result (dot / decorator); neutral grey when undocumented. */
export const getNetworkScanColor = (state: string | null | undefined): string =>
    (state && CONFIG_BY_STATE[state]?.color) || FALLBACK_COLOR;

/** Only GREEN admits a new IoT connection; ORANGE, RED and "not documented" block. */
export const isNetworkScanPassed = (state: string | null | undefined): boolean =>
    state === NetworkScanState.GREEN;

/** Wire format of `Date` scalars (`scanDate`). */
export const SCAN_DATE_FORMAT = "YYYY-MM-DD";

/**
 * `2026-08-01` -> `01.08.2026`. Always parses with the explicit wire format:
 * `moment(string)` on a date-only value is UTC-based and can shift a day
 * around DST changes. Unparseable input is returned as-is.
 */
export const formatScanDate = (scanDate: string | null | undefined): string => {
    if (!scanDate) return "";
    const parsed = moment(scanDate, SCAN_DATE_FORMAT, true);
    return parsed.isValid() ? parsed.format("DD.MM.YYYY") : scanDate;
};
